import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Minus, Plus, Printer, ArrowLeft, ArrowRight } from "lucide-react";
import { getChapter, getPopulatedChapters } from "../data/chapters";
import { NEW_TESTAMENT, OLD_TESTAMENT, slugify } from "../data/books";
import PlaceholderChapter from "./PlaceholderChapter";

const STORAGE_KEY = "haydock.readerScale";
const MIN = 0.85;
const MAX = 1.6;
const STEP = 0.075;

function clamp(v) {
  return Math.min(MAX, Math.max(MIN, Number(v.toFixed(3))));
}

function findBook(slug) {
  const all = [...NEW_TESTAMENT, ...OLD_TESTAMENT];
  return (
    all.find((b) => slugify(b.name) === slug) ||
    all.find((b) => slugify(b.name.replace(/^st\.?\s+/i, "")) === slug) ||
    null
  );
}

export default function ChapterReader() {
  const { slug, chapter: chapterParam } = useParams();
  const navigate = useNavigate();
  const chapterData = getChapter(slug, chapterParam);
  const bookMeta = findBook(slug);

  const [scale, setScale] = useState(() => {
    if (typeof window === "undefined") return 1;
    const saved = parseFloat(localStorage.getItem(STORAGE_KEY) || "1");
    return isNaN(saved) ? 1 : clamp(saved);
  });
  const [activeVerse, setActiveVerse] = useState(null);
  const [hoveredVerse, setHoveredVerse] = useState(null);

  useEffect(() => {
    document.documentElement.style.setProperty("--reader-scale", String(scale));
    try {
      localStorage.setItem(STORAGE_KEY, String(scale));
    } catch {
      /* ignore */
    }
  }, [scale]);

  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty("--reader-scale", "1");
    };
  }, []);

  // Reset active verse and scroll to top when chapter changes
  useEffect(() => {
    setActiveVerse(null);
    setHoveredVerse(null);
    // Honour #v=N hash for deep-links from search results
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const m = hash.match(/^#v=(\d{1,3})$/);
    if (m) {
      const n = parseInt(m[1], 10);
      setActiveVerse(n);
      // Defer scroll until the chapter rows have rendered
      requestAnimationFrame(() => {
        const el = document.getElementById(`verse-anchor-${n}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else if (typeof window !== "undefined") {
      window.scrollTo({ top: 0 });
    }
  }, [slug, chapterParam]);

  const chapterNum = chapterData ? chapterData.chapter : parseInt(chapterParam, 10);
  const totalChapters = bookMeta ? bookMeta.chapters : null;
  const prevChapterUrl =
    chapterNum > 1
      ? `/book/${slug}/chapter/${chapterNum - 1}`
      : `/book/${slug}/introduction`;
  const nextChapterUrl =
    totalChapters && chapterNum < totalChapters
      ? `/book/${slug}/chapter/${chapterNum + 1}`
      : null;

  // Keyboard shortcuts — bind once. Read the latest URL & chapter data
  // synchronously inside the handler so rapid presses use up-to-date values
  // (React Router's pushState is synchronous; window.location updates
  // immediately even if a re-render hasn't happened yet).
  const chapterCacheRef = useRef({});
  chapterCacheRef.current.hasChapter = !!chapterData;
  chapterCacheRef.current.lastVerse = chapterData ? chapterData.verses.length : 0;

  useEffect(() => {
    const scrollToVerse = (n) => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`verse-anchor-${n}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };

    const resolveNav = () => {
      const path = window.location.pathname;
      const m = path.match(/^\/book\/([^/]+)\/chapter\/(\d+)/);
      if (!m) return { prev: null, next: null };
      const slug = m[1];
      const n = parseInt(m[2], 10);
      const book = findBook(slug);
      const total = book ? book.chapters : null;
      return {
        prev:
          n > 1 ? `/book/${slug}/chapter/${n - 1}` : `/book/${slug}/introduction`,
        next: total && n < total ? `/book/${slug}/chapter/${n + 1}` : null,
      };
    };

    const handler = (e) => {
      const t = e.target;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const { hasChapter, lastVerse } = chapterCacheRef.current;

      switch (e.key) {
        case "ArrowDown":
        case "j":
          if (!hasChapter) return;
          e.preventDefault();
          setActiveVerse((p) => {
            const n = p ? Math.min(lastVerse, p + 1) : 1;
            scrollToVerse(n);
            return n;
          });
          break;
        case "ArrowUp":
        case "k":
          if (!hasChapter) return;
          e.preventDefault();
          setActiveVerse((p) => {
            const n = p ? Math.max(1, p - 1) : 1;
            scrollToVerse(n);
            return n;
          });
          break;
        case "ArrowLeft": {
          const { prev } = resolveNav();
          if (!prev) return;
          e.preventDefault();
          navigate(prev);
          break;
        }
        case "ArrowRight": {
          const { next } = resolveNav();
          if (!next) return;
          e.preventDefault();
          navigate(next);
          break;
        }
        case "Home":
          if (!hasChapter) return;
          e.preventDefault();
          setActiveVerse(1);
          scrollToVerse(1);
          break;
        case "End":
          if (!hasChapter) return;
          e.preventDefault();
          setActiveVerse(lastVerse);
          scrollToVerse(lastVerse);
          break;
        case "Escape":
          setActiveVerse(null);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  // Chapter not transcribed yet → render the placeholder
  if (!chapterData) {
    return <PlaceholderChapter />;
  }

  const { book, argument, verses, commentary } = chapterData;
  const commByVerse = new Map();
  commentary.forEach((c) => commByVerse.set(c.verse, c.notes));
  const populated = getPopulatedChapters(slug);

  const jumpTo = (n) => {
    setActiveVerse(n);
    const el = document.getElementById(`verse-anchor-${n}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="chapter-page" className="max-w-7xl mx-auto px-6 py-10">
      {/* Toolbar */}
      <div
        className="no-print flex flex-wrap items-center justify-between gap-3 mb-6"
        data-testid="reader-toolbar"
      >
        <nav
          aria-label="Breadcrumb"
          style={{
            fontSize: "0.95rem",
            color: "var(--ink-muted)",
            fontStyle: "italic",
          }}
        >
          <Link to="/" style={{ color: "var(--ink-soft)" }}>
            Home
          </Link>{" "}
          <span>&rsaquo;</span>{" "}
          <Link
            to={`/book/${slug}/introduction`}
            style={{ color: "var(--ink-soft)" }}
          >
            {book}
          </Link>{" "}
          <span>&rsaquo;</span>{" "}
          <span style={{ color: "var(--ink)" }}>Chapter {chapterNum}</span>
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setScale((s) => clamp(s - STEP))}
            disabled={scale <= MIN + 0.001}
            data-testid="font-decrease"
            aria-label="Decrease font size"
            title="Decrease font size"
          >
            <Minus size={14} />
            <span style={{ fontSize: "0.78rem" }}>A</span>
          </button>
          <span
            data-testid="font-scale-indicator"
            style={{
              minWidth: "3.2rem",
              textAlign: "center",
              fontFamily: "'IM Fell English SC', serif",
              fontSize: "0.85rem",
              color: "var(--ink-muted)",
            }}
          >
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setScale((s) => clamp(s + STEP))}
            disabled={scale >= MAX - 0.001}
            data-testid="font-increase"
            aria-label="Increase font size"
            title="Increase font size"
          >
            <Plus size={14} />
            <span style={{ fontSize: "1rem" }}>A</span>
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => window.print()}
            data-testid="print-button"
            aria-label="Print this chapter"
            title="Print this chapter"
          >
            <Printer size={14} /> PRINT
          </button>
        </div>
      </div>

      {/* Chapter header */}
      <header className="text-center mb-8 fade-in">
        <div
          style={{
            fontFamily: "'IM Fell English SC', serif",
            letterSpacing: "0.3em",
            color: "var(--ink-muted)",
            fontSize: "0.85rem",
          }}
        >
          {book.toUpperCase()}
        </div>
        <h1
          style={{
            fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
            margin: "0.4rem 0 0.6rem",
            letterSpacing: "0.04em",
          }}
        >
          Chapter {chapterNum}
        </h1>
        <p
          style={{
            fontFamily: "'IM Fell English', serif",
            fontStyle: "italic",
            color: "var(--ink-soft)",
            fontSize: "1.15rem",
            maxWidth: "55ch",
            margin: "0 auto",
          }}
        >
          {argument}
        </p>
        <div className="ornament mt-6" aria-hidden>
          <span>&#10086;</span>
        </div>
      </header>

      {/* Two-pane: verse rail + content */}
      <div className="reader-layout">
        <aside
          className="verse-rail no-print"
          data-testid="verse-rail"
          aria-label="Jump to verse"
        >
          <div className="verse-rail-inner">
            <div className="rail-label">VERSES</div>
            <div className="rail-pills">
              {verses.map((v) => {
                const hasNote = commByVerse.has(v.n);
                const isActive = activeVerse === v.n;
                return (
                  <button
                    key={v.n}
                    type="button"
                    onClick={() => jumpTo(v.n)}
                    className={`verse-pill${isActive ? " is-active" : ""}${hasNote ? "" : " no-note"}`}
                    data-testid={`rail-pill-${v.n}`}
                    aria-label={`Jump to verse ${v.n}${hasNote ? " (has commentary)" : ""}`}
                    title={`Verse ${v.n}${hasNote ? " — commentary available" : ""}`}
                  >
                    {v.n}
                  </button>
                );
              })}
            </div>
            <div className="rail-legend" aria-hidden>
              <span className="dot dot-note" /> with note
              <span className="dot dot-empty" /> none
            </div>
          </div>
        </aside>

        <div className="reader-content min-w-0">
          <div className="parallel-headers no-print" aria-hidden>
            <div>BIBLE TEXT</div>
            <div>NOTES &amp; COMMENTARY</div>
          </div>

          <section
            className="parchment-card fade-in"
            data-testid="parallel-section"
            style={{ padding: 0, overflow: "hidden" }}
          >
            <div className="parallel-grid" data-testid="parallel-grid">
              {verses.map((v, idx) => {
                const notes = commByVerse.get(v.n);
                const isFirst = idx === 0;
                const isActive = activeVerse === v.n;
                const isHover = hoveredVerse === v.n;
                const rowState = `${isActive ? " is-active" : ""}${isHover ? " is-hover" : ""}`;

                return (
                  <div
                    key={v.n}
                    id={`verse-anchor-${v.n}`}
                    className="parallel-row"
                    data-testid={`verse-row-${v.n}`}
                    onMouseEnter={() => setHoveredVerse(v.n)}
                    onMouseLeave={() => setHoveredVerse(null)}
                    onClick={() =>
                      setActiveVerse((prev) => (prev === v.n ? null : v.n))
                    }
                  >
                    <div
                      className={`verse-cell reader${isFirst ? " dropcap" : ""}${rowState}`}
                      data-testid={`verse-cell-${v.n}`}
                    >
                      <p>
                        <span
                          className="verse-num"
                          data-testid={`verse-num-${v.n}`}
                        >
                          {v.n}
                        </span>
                        <span data-testid={`verse-text-${v.n}`}>{v.text}</span>
                      </p>
                    </div>
                    <div
                      className={`comm-cell${rowState}`}
                      data-testid={`comm-cell-${v.n}`}
                    >
                      {notes ? (
                        <div
                          className="space-y-3"
                          data-testid={`commentary-verse-${v.n}`}
                        >
                          <div className="commentary-verse-label">
                            VERSE {v.n}
                          </div>
                          {notes.map((note, i) => (
                            <p
                              key={i}
                              className="commentary-note"
                              style={{ margin: 0 }}
                              data-testid={`commentary-note-${v.n}-${i}`}
                            >
                              {note.text}{" "}
                              <span className="attribution">
                                &mdash; {note.attribution}
                              </span>
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p
                          className="commentary-empty"
                          data-testid={`commentary-empty-${v.n}`}
                        >
                          &mdash; no note &mdash;
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      {/* Pagination */}
      <nav
        className="no-print mt-12 flex flex-wrap items-center justify-between gap-3"
        data-testid="chapter-pagination"
        aria-label="Chapter navigation"
      >
        <Link
          to={prevChapterUrl}
          className="btn-ghost"
          data-testid="prev-chapter"
        >
          <ArrowLeft size={14} />{" "}
          {chapterNum > 1 ? `CHAPTER ${chapterNum - 1}` : "INTRODUCTION"}
        </Link>
        <Link to="/" className="btn-ghost" data-testid="back-to-index">
          BACK TO INDEX
        </Link>
        {nextChapterUrl ? (
          <Link
            to={nextChapterUrl}
            className="btn-ghost"
            data-testid="next-chapter"
          >
            CHAPTER {chapterNum + 1} <ArrowRight size={14} />
          </Link>
        ) : (
          <span
            className="btn-ghost"
            style={{ opacity: 0.4, cursor: "default" }}
            data-testid="next-chapter-end"
          >
            END OF BOOK
          </span>
        )}
      </nav>

      {/* Populated chapters quick-nav */}
      {populated.length > 1 && (
        <div
          className="no-print mt-6 text-center"
          data-testid="populated-quick-nav"
          style={{ fontSize: "0.9rem", color: "var(--ink-muted)" }}
        >
          <span style={{ fontStyle: "italic" }}>Transcribed in {book}: </span>
          {populated.map((n, i) => (
            <span key={n}>
              {n === chapterNum ? (
                <strong style={{ color: "var(--oxblood)" }}>{n}</strong>
              ) : (
                <Link to={`/book/${slug}/chapter/${n}`}>{n}</Link>
              )}
              {i < populated.length - 1 ? " · " : ""}
            </span>
          ))}
        </div>
      )}

      {/* Keyboard shortcut hint */}
      <div
        className="no-print kbd-hints"
        data-testid="kbd-hints"
        aria-label="Keyboard shortcuts"
      >
        <span><kbd>&uarr;</kbd><kbd>&darr;</kbd> verse</span>
        <span className="kbd-sep">&middot;</span>
        <span><kbd>&larr;</kbd><kbd>&rarr;</kbd> chapter</span>
        <span className="kbd-sep">&middot;</span>
        <span><kbd>Home</kbd><kbd>End</kbd> first / last</span>
        <span className="kbd-sep">&middot;</span>
        <span><kbd>Esc</kbd> clear</span>
      </div>
    </div>
  );
}
